import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AudioLines, Ban } from "lucide-react";
import { MAX_UPLOAD_BYTES } from "@protocol";
import { isBusy, useRecorder } from "@/recorder/context";
import { dragHasFiles, droppedFiles, formatBytes } from "@/recorder/audioFile";
import { DropTargetContext, type DropTarget, type DropTargetApi } from "@/recorder/dropTarget";

/**
 * Drop an audio file anywhere in the app to upload it instead of recording.
 * Several at once are asked about - one recording or several - first.
 *
 * The listeners sit on `window` and the overlay itself ignores the pointer, so
 * there is no small target to hit: the overlay only says where the file will
 * go. It also swallows every file drop - without that, a file released over
 * the page makes the browser open it and navigate away, mid-lecture.
 */

/**
 * `dragover` repeats every few hundred ms while a drag is over the page; if it
 * stops without a `dragleave` (Escape in some browsers) the overlay still goes.
 */
const DRAG_WATCHDOG_MS = 1_200;

export default function FileDropProvider({ children }: { children: ReactNode }) {
  const recorder = useRecorder();
  const [targets, setTargets] = useState<Array<{ key: number; target: DropTarget }>>([]);
  const nextKey = useRef(0);
  const [dragging, setDragging] = useState(false);

  const api = useMemo<DropTargetApi>(
    () => ({
      register: (target) => {
        const key = nextKey.current;
        nextKey.current += 1;
        setTargets((current) => [...current, { key, target }]);
        return () => setTargets((current) => current.filter((entry) => entry.key !== key));
      },
    }),
    [],
  );

  const target = targets.length > 0 ? targets[targets.length - 1].target : null;
  const blocked =
    isBusy(recorder.status) || recorder.status === "requesting" || recorder.batch !== null;

  // The listeners are bound once; they read the current recorder and target
  // through this ref instead of re-binding on every progress tick.
  const latest = useRef({ recorder, target, blocked });
  useEffect(() => {
    latest.current = { recorder, target, blocked };
  });

  useEffect(() => {
    let depth = 0;
    let watchdog: ReturnType<typeof setTimeout> | null = null;
    const hide = () => {
      depth = 0;
      if (watchdog !== null) clearTimeout(watchdog);
      watchdog = null;
      setDragging(false);
    };
    const keepAlive = () => {
      if (watchdog !== null) clearTimeout(watchdog);
      watchdog = setTimeout(hide, DRAG_WATCHDOG_MS);
    };

    const onEnter = (event: DragEvent) => {
      if (!dragHasFiles(event.dataTransfer)) return;
      event.preventDefault();
      depth += 1;
      setDragging(true);
      keepAlive();
    };
    const onOver = (event: DragEvent) => {
      if (!dragHasFiles(event.dataTransfer)) return;
      event.preventDefault();
      if (event.dataTransfer !== null) {
        event.dataTransfer.dropEffect = latest.current.blocked ? "none" : "copy";
      }
      setDragging(true);
      keepAlive();
    };
    const onLeave = (event: DragEvent) => {
      if (!dragHasFiles(event.dataTransfer)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) hide();
    };
    const onDrop = (event: DragEvent) => {
      if (!dragHasFiles(event.dataTransfer)) return;
      event.preventDefault();
      hide();
      const files = droppedFiles(event.dataTransfer);
      const current = latest.current;
      if (files.length === 0 || current.blocked) return;
      void current.recorder.uploadFiles(files, current.target?.lessonId ?? null);
    };

    window.addEventListener("dragenter", onEnter);
    window.addEventListener("dragover", onOver);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      if (watchdog !== null) clearTimeout(watchdog);
      window.removeEventListener("dragenter", onEnter);
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("drop", onDrop);
    };
  }, []);

  return (
    <DropTargetContext.Provider value={api}>
      {children}
      <AnimatePresence>
        {dragging && (
          <DropOverlay key="file-drop" blocked={blocked} target={target} />
        )}
      </AnimatePresence>
    </DropTargetContext.Provider>
  );
}

function DropOverlay({ blocked, target }: { blocked: boolean; target: DropTarget | null }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.16 }}
      className="no-print pointer-events-none fixed inset-0 z-[80] flex items-center justify-center bg-[#fdfdfc]/70 p-6 backdrop-blur-sm dark:bg-[#0f0f0e]/75"
    >
      <motion.div
        initial={{ scale: 0.94, y: 10, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.97, y: 6, opacity: 0 }}
        transition={{ type: "spring", stiffness: 420, damping: 32 }}
        className="relative w-full max-w-md rounded-3xl bg-[#fdfdfc] px-8 py-10 text-center shadow-2xl dark:bg-[#141413]"
      >
        {/* Marching dashed border: an SVG rect, so the dashes can move. */}
        <svg className="absolute inset-0 h-full w-full overflow-visible" aria-hidden>
          <rect
            x="1"
            y="1"
            rx="23"
            fill="none"
            strokeWidth="1.5"
            strokeDasharray="8 6"
            style={{ width: "calc(100% - 2px)", height: "calc(100% - 2px)" }}
            className={
              blocked
                ? "stroke-neutral-300 dark:stroke-neutral-700"
                : "lls-march stroke-neutral-400 dark:stroke-neutral-500"
            }
          />
        </svg>

        <div className="relative mx-auto mb-5 grid h-16 w-16 place-items-center">
          {!blocked && (
            <motion.span
              className="absolute inset-0 rounded-2xl bg-[#222]/10 dark:bg-white/10"
              animate={{ scale: [1, 1.45], opacity: [0.6, 0] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut" }}
            />
          )}
          <motion.span
            className={`relative grid h-16 w-16 place-items-center rounded-2xl ${
              blocked
                ? "bg-neutral-200 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400"
                : "bg-[#222] text-[#fdfdfc] dark:bg-[#ededeb] dark:text-[#141413]"
            }`}
            animate={blocked ? { y: 0 } : { y: [0, -5, 0] }}
            transition={
              blocked ? { duration: 0.2 } : { duration: 1.6, repeat: Infinity, ease: "easeInOut" }
            }
          >
            {blocked ? <Ban className="h-7 w-7" /> : <AudioLines className="h-7 w-7" />}
          </motion.span>
        </div>

        <h2 className="text-lg font-semibold tracking-tight text-[#222] dark:text-[#ededeb]">
          {blocked ? "Recorder is busy" : "Drop to upload"}
        </h2>
        <p className="mx-auto mt-1.5 max-w-xs text-sm text-neutral-500 dark:text-neutral-400">
          {blocked ? (
            "Finish or discard the current recording first."
          ) : target !== null ? (
            <>
              Attaches to{" "}
              <span className="font-medium text-[#222] dark:text-[#ededeb]">{target.label}</span>
            </>
          ) : (
            "Matched to your timetable by the file's time, or left in Unassigned."
          )}
        </p>
        {!blocked && (
          <p className="mt-4 font-mono text-[10px] tracking-wide text-neutral-400 uppercase">
            mp3 · m4a · wav · flac · ogg · mp4 video · up to {formatBytes(MAX_UPLOAD_BYTES)}
          </p>
        )}
      </motion.div>
    </motion.div>
  );
}
